"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import { mockTemplates, templateTypes } from "@/app/(public)/(resources)/templates/mockData";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import { Button } from "@/app/(public)/shared/ui/Button";
import { useUser } from "@/hooks/useUser";
import { saveTemplateDownload } from "@/lib/database/client/database";
import type { Template } from "@/lib/publications/types";
import { createClient } from "@/utils/supabase/client";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Download, Filter, Mail, Search, Tag } from "lucide-react";
import Link from "next/link";
import { useMemo, useState } from "react";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

interface TemplateCardProps {
  template: Template;
  onDownload: (template: Template) => void;
}

function TemplateCard({ template, onDownload }: TemplateCardProps) {
  const { openModal } = useModalManager();

  const handleDownload = () => {
    console.log("Download clicked for template:", template.title);
    onDownload(template);
  };

  return (
    <motion.div
      variants={fadeInUp}
      className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden flex flex-col h-full"
    >
      <div className="relative">
        <img src={template.previewImage} alt={template.title} className="w-full h-48 object-cover" />
      </div>

      <div className="p-6 flex flex-col flex-1">
        {/* Top section: Title and Description */}
        <div className="flex-1">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-h4 font-semibold text-gray-900 line-clamp-2">{template.title}</h3>
          </div>

          <p className="text-body text-gray-600 line-clamp-3">{template.description}</p>
        </div>

        {/* Bottom section: Metadata, Tags, and Buttons */}
        <div className="mt-6">
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
            <div className="flex items-center gap-1">
              <Download className="w-4 h-4" />
              <span>{template.downloadCount.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1">
              <Tag className="w-4 h-4" />
              <span>{template.fileFormat}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{template.fileSize}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {template.useCases.slice(0, 2).map((useCase, index) => (
              <span key={index} className="px-2 py-1 border border-gray-300 rounded text-xs bg-white">
                {useCase}
              </span>
            ))}
            {template.useCases.length > 2 && (
              <span className="px-2 py-1 border border-gray-300 rounded text-xs bg-white">
                +{template.useCases.length - 2} more
              </span>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => (window.location.href = `/templates/${template.slug}`)}
              className="flex-1"
            >
              View Template
            </Button>
            <Button variant="outline" size="sm" leftIcon={<Download className="w-4 h-4" />} onClick={handleDownload}>
              Download
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Templates() {
  const { openModal, closeModal, isModalOpen } = useModalManager();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("All Types");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showMoreTags, setShowMoreTags] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const { user } = useUser();
  const supabase = createClient();

  const handleDownload = (template: Template) => {
    if (user) {
      if (template.downloadUrl) {
        window.open(template.downloadUrl, "_blank");
      } else {
        openModal("demo");
      }
    } else {
      setSelectedTemplate(template);
      openModal("email-collection");
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !selectedTemplate) return;

    console.log("Submitting email for template:", selectedTemplate.title);
    console.log("Email:", email);

    setIsSubmitting(true);

    try {
      await saveTemplateDownload(supabase, email, selectedTemplate.title);
      console.log("Email saved successfully");
      closeModal("email-collection");
      setEmail("");
      setSelectedTemplate(null);

      // Redirect to the template PDF
      if (selectedTemplate.downloadUrl) {
        console.log("Redirecting to PDF:", selectedTemplate.downloadUrl);
        window.open(selectedTemplate.downloadUrl, "_blank");
      } else {
        console.log("No download URL found, opening demo modal");
        openModal("demo");
      }
    } catch (error) {
      console.error("Error saving email:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const listedTemplates = useMemo(() => mockTemplates.filter((t) => !t.unlisted), []);

  // Get all unique useCases from templates for the tag filter
  const allUseCases = useMemo(() => {
    const useCases = new Set<string>();
    listedTemplates.forEach((template) => {
      template.useCases.forEach((useCase) => useCases.add(useCase));
    });
    return Array.from(useCases).sort();
  }, [listedTemplates]);

  // Limit tags shown initially to 8
  const visibleTags = showMoreTags ? allUseCases : allUseCases.slice(0, 8);

  const filteredTemplates = useMemo(() => {
    return listedTemplates.filter((template) => {
      const matchesSearch =
        template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
        template.useCases.some((useCase) => useCase.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesType = selectedType === "All Types" || template.templateType === selectedType;

      const matchesTags =
        selectedTags.length === 0 || selectedTags.some((selectedTag) => template.useCases.includes(selectedTag));

      return matchesSearch && matchesType && matchesTags;
    });
  }, [listedTemplates, searchTerm, selectedType, selectedTags]);

  const featuredTemplates = listedTemplates.filter((template) => template.featured);

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  return (
    <div className="bg-landing-primary">
      {/* Hero Section */}
      <section className="bg-landing-primary pt-32 pb-16">
        <div className="container-page">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-h1 font-bold text-gray-900 mb-4">Event Planning Templates</h1>
            <p className="text-h4 text-gray-600 mb-8">
              Download professional templates to streamline your event planning process. From checklists to budget
              trackers, everything you need to plan successful events.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="primary"
                size="lg"
                onClick={() => {
                  document.getElementById("all-templates")?.scrollIntoView({ behavior: "smooth" });
                }}
                rightIcon={<ArrowRight className="w-5 h-5" />}
              >
                Browse All Templates
              </Button>
              <Link href="/create">
                <Button variant="outline" size="lg">
                  Try our platform
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Templates */}
      <section className="py-16">
        <div className="container-page">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-h2 font-bold text-gray-900 mb-4">Most Popular Templates</h2>
            <p className="text-body text-gray-600 max-w-2xl">
              Get started with our most downloaded templates. These proven resources will help you plan better events
              with less stress.
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {featuredTemplates.map((template) => (
              <TemplateCard key={template.id} template={template} onDownload={handleDownload} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* All Templates Section */}
      <section id="all-templates" className="py-16 bg-gray-50">
        <div className="container-page">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <h2 className="text-h2 font-bold text-gray-900 mb-4">All Templates</h2>
            <p className="text-body text-gray-600 mb-8">
              Explore our complete collection of event planning templates, organized by category and type.
            </p>

            {/* Search and Filters */}
            <div className="flex flex-col lg:flex-row gap-4 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex gap-4">
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {templateTypes.map((type) => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tag Filter */}
            {allUseCases.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Filter by Tags:</h3>
                <div className="flex flex-wrap gap-2">
                  {visibleTags.map((useCase) => (
                    <button
                      key={useCase}
                      onClick={() => handleTagToggle(useCase)}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                        selectedTags.includes(useCase)
                          ? "bg-blue-500 text-white border-blue-500"
                          : "bg-white text-gray-700 border-gray-300 hover:border-blue-300"
                      }`}
                    >
                      {useCase}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2 mt-3">
                  {!showMoreTags && allUseCases.length > 8 && (
                    <button onClick={() => setShowMoreTags(true)} className="text-sm text-blue-600 hover:text-blue-800">
                      Show more tags ({allUseCases.length - 8} more)
                    </button>
                  )}
                  {showMoreTags && (
                    <button
                      onClick={() => setShowMoreTags(false)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Show less
                    </button>
                  )}
                  {selectedTags.length > 0 && (
                    <button onClick={() => setSelectedTags([])} className="text-sm text-blue-600 hover:text-blue-800">
                      Clear all tags
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Results Count */}
            <p className="text-sm text-gray-600 mb-6">
              Showing {filteredTemplates.length} of {listedTemplates.length} templates
            </p>
          </motion.div>

          {/* Templates Grid */}
          {filteredTemplates.length > 0 ? (
            <motion.div
              variants={stagger}
              initial="initial"
              animate="animate"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredTemplates.map((template) => (
                <TemplateCard key={template.id} template={template} onDownload={handleDownload} />
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Filter className="w-12 h-12 mx-auto" />
              </div>
              <h3 className="text-h4 font-semibold text-gray-900 mb-2">No templates found</h3>
              <p className="text-body text-gray-600 mb-4">Try adjusting your search or filter criteria</p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedType("All Types");
                  setSelectedTags([]);
                  setShowMoreTags(false);
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container-page">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl p-8 md:p-12 text-center"
            style={{
              backgroundColor: "var(--landing-bg-primary-purple)",
              color: "var(--landing-text-primary)",
            }}
          >
            <h2 className="text-h2 font-bold mb-4">Need More Than Templates?</h2>
            <p className="text-h4 mb-8 max-w-2xl mx-auto">
              Let Nowadays handle the entire event planning process for you. From venue sourcing to attendee management,
              we've got you covered.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/create">
                <Button variant="primary" size="lg">
                  Try our platform
                </Button>
              </Link>
              <Button variant="outline" size="lg" onClick={() => (window.location.href = "/venue-sourcing")}>
                Learn About Our Services
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Demo Modal */}
      <UnifiedModal
        type="cal"
        isOpen={isModalOpen("demo")}
        onClose={() => closeModal("demo")}
        title="Book a Demo"
        subtitle="Schedule a 30-minute demo with our team"
        size="lg"
        calLink={DEMO_CALENDAR_LINK}
        fallbackText="Book your demo with our team"
      />

      {/* Email Collection Modal */}
      <UnifiedModal
        type="content"
        isOpen={isModalOpen("email-collection")}
        onClose={() => closeModal("email-collection")}
        title="Get Your Free Template"
        subtitle="Enter your email to download this template"
        size="sm"
      >
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Download {selectedTemplate?.title}</h3>
            <p className="text-gray-600">
              We'll send you the template and keep you updated with our latest resources and tips.
            </p>
          </div>

          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => closeModal("email-collection")} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting || !email} className="flex-1">
                {isSubmitting ? "Downloading..." : "Download Template"}
              </Button>
            </div>
          </form>
        </div>
      </UnifiedModal>
    </div>
  );
}
